import { useEffect, useState } from "react";
import api from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useNavigate } from "react-router-dom";

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

export default function MDDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [g, setG] = useState(null);
  useEffect(() => {
    api.get("/analytics/md-dashboard").then((r) => setD(r.data));
    api.get("/analytics/group-overview").then((r) => setG(r.data));
  }, []);
  if (!d) return <div className="p-8 text-slate-400">Loading group view…</div>;
  const s = d.status_counts || {};
  const delta = d.td_last_month ? Math.round((d.td_this_month - d.td_last_month) / d.td_last_month * 100) : null;

  return (
    <div className="px-4 py-5 lg:px-8 max-w-6xl">
      <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Group Overview <span className="text-sm font-normal text-slate-400">· MD view · {d.month}</span></h1>

      <div data-testid="md-headline-strip" className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {[["Total Fleet", d.total_fleet, "text-[#0E2240]"], ["Available", s.available || 0, "text-emerald-600"],
          ["On TD Now", s.on_test_drive || 0, "text-blue-600"], ["Workshop", s.workshop || 0, "text-[#BB162B]"],
          ["Grounded", (s.grounded || 0) + (s.in_transit || 0), "text-slate-500"], ["Non-Compliant", d.non_compliant, "text-amber-600"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
            <div className={`font-heading text-xl font-bold ${c}`}>{v}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl bg-[#0E2240] p-5 text-white">
          <div className="text-[10px] uppercase tracking-widest text-[#BF9B30]">Test drives this month</div>
          <div className="font-heading text-4xl font-bold">{d.td_this_month}</div>
          <div className="mt-1 text-xs text-slate-300">
            {delta !== null && <span className={delta >= 0 ? "text-emerald-400" : "text-red-400"}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}%</span>} vs last month ({d.td_last_month}) · {d.home_td_month} home / {d.td_this_month - d.home_td_month} showroom · {d.additional_model_td_month || 0} additional-model · avg {d.avg_td_per_car}/car
          </div>
        </div>
        <div className="rounded-xl bg-[#BB162B] p-5 text-white">
          <div className="text-[10px] uppercase tracking-widest text-white/70">Monthly outflow</div>
          <div className="font-heading text-4xl font-bold">{inr(d.monthly_outflow.total)}</div>
          <div className="mt-1 text-xs text-white/80">EMI {inr(d.monthly_outflow.emi)} · Fuel {inr(d.monthly_outflow.fuel)} · Repair {inr(d.monthly_outflow.repair)} · Other {inr(d.monthly_outflow.other)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Avg cost / km (90d)</div>
          <div className="font-heading text-4xl font-bold text-[#0E2240]">₹{d.avg_cost_per_km}</div>
          <div className="mt-1 text-xs text-slate-400">{d.transfers_open} transfers open across the group</div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-[#BB162B]">Red Flags</h2>
        <div data-testid="md-red-flags" className="mt-2 grid grid-cols-3 gap-2">
          {[["Odo mismatches", d.red_flags.odo_mismatches, "/exceptions"], ["Gate overrides", d.red_flags.gate_overrides, "/exceptions"],
            ["Overdue trips", d.red_flags.overdue_trips, "/exceptions"], ["Workshop overdue", d.red_flags.workshop_overdue, "/workshop"],
            ["Under-utilized", d.red_flags.under_utilized, "/accounts"], ["Transfer overdue", d.red_flags.transfers_overdue, "/transfers"]].map(([l, v, link]) => (
            <button key={l} onClick={() => nav(link)} className="rounded-lg bg-white border border-red-100 p-2.5 text-left card-hover">
              <div className="font-heading text-xl font-bold text-[#BB162B]">{v}</div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{l}</div>
            </button>
          ))}
        </div>
        </div>
        <div data-testid="md-disposal-tile" className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Disposal</h2>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {[["Pending disposal", d.disposal?.pending ?? 0, "/accounts"], ["Sold this quarter", d.disposal?.sold_this_quarter ?? 0, "/accounts"],
              ["Avg net cost / car (qtr)", d.disposal?.sold_this_quarter ? inr(d.disposal?.avg_net_lifetime_cost) : "—", "/accounts"]].map(([l, v, link]) => (
              <button key={l} onClick={() => nav(link)} className="rounded-lg bg-slate-50 border border-slate-100 p-2.5 text-left card-hover">
                <div className="font-heading text-xl font-bold text-[#0E2240]">{v}</div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{l}</div>
              </button>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-slate-400">Cars pending disposal lose value monthly while EMI may still run.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Top / bottom branches (TD per vehicle)</h2>
          <div className="mt-2 space-y-1.5">
            {d.top_branches.map((b) => (
              <div key={b.branch} className="flex justify-between rounded-lg bg-emerald-50 p-2.5 text-xs">
                <span className="font-semibold text-[#0E2240]">{b.branch}</span>
                <span className="text-emerald-700 font-bold">{b.td_month} TDs · {b.td_per_vehicle}/car</span>
              </div>
            ))}
            {d.bottom_branches.filter((b) => !d.top_branches.some((t) => t.branch === b.branch)).map((b) => (
              <div key={b.branch} className="flex justify-between rounded-lg bg-red-50 p-2.5 text-xs">
                <span className="font-semibold text-[#0E2240]">{b.branch}</span>
                <span className="text-[#BB162B] font-bold">{b.td_month} TDs · {b.td_per_vehicle}/car</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">10 most expensive cars per km (90d)</h2>
          <div className="mt-2 space-y-1.5">
            {d.most_expensive.map((v, i) => (
              <div key={v.reg_no} className="flex justify-between rounded-lg bg-slate-50 p-2 text-xs">
                <span className="font-semibold text-[#0E2240]">{i + 1}. {v.reg_no} · {v.model} <span className="text-slate-400">({v.branch})</span></span>
                <span className="font-bold text-[#BB162B]">₹{v.cost_per_km}/km</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Test drive trend</h2>
          <div className="mt-3 h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.td_trend}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="tds" stroke="#BB162B" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">Zonal Head → dealer code drill-down</h2>
          {g?.zones.map((z) => (
            <div key={z.zone_id} className="mt-2">
              <div className="text-xs font-bold text-[#0E2240]">{z.zone_name} <span className="text-slate-400 font-normal">· {z.fleet} cars · {z.td_month} TDs</span></div>
              <div className="mt-1 space-y-1">
                {z.branches.map((b) => (
                  <button key={b.branch_id} onClick={() => nav(`/board?branch=${b.branch_id}`)}
                    className="flex w-full justify-between rounded-lg bg-slate-50 p-2 text-[11px] card-hover">
                    <span className="font-semibold text-slate-700">{b.branch}</span>
                    <span className="text-slate-500">{b.fleet} cars · {b.td_month} TDs · {b.idle_30d} idle · {b.workshop} WS</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
